import React, { useState, useRef } from "react";
import api from "../api";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";

const GuestUpload = ({ eventId, onUpload, eventData }) => {
  const { showToast } = useNotification();
  const [files, setFiles] = useState([]);
  const [guestName, setGuestName] = useState(() => localStorage.getItem("youmak_guest_name") || "");
  const [mediaType, setMediaType] = useState("photo");
  const [textContent, setTextContent] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [showViralCTA, setShowViralCTA] = useState(false);

  const mediaRecorderRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        setAudioBlob(blob);
        const file = new File([blob], "recording.webm", { type: "audio/webm" });
        setFiles([file]);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Recording error:", err);
      showToast("مش عارف أوصل للميكروفون، اتأكد من الصلاحيات.", "danger");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
  };

  const handleUpload = async () => {
    if (!guestName.trim()) {
      showToast("من فضلك اكتب اسمك.", "danger");
      return;
    }

    if (mediaType === "text" && !textContent.trim()) {
      showToast("من فضلك اكتب رسالتك أو التهنئة.", "danger");
      return;
    }

    if (mediaType !== "text" && files.length === 0) {
      showToast("من فضلك اختار ملف أو أكثر.", "danger");
      return;
    }

    setLoading(true);
    setUploadProgress({ current: 0, total: files.length || 1 });

    try {
      localStorage.setItem("youmak_guest_name", guestName);
      if (eventData?.ownerId?.plan === 'free' && (mediaType === 'photo' || !mediaType)) {
        // We'll check the current count on the backend, but we can also do a quick check here if we had the count
      }
      if (mediaType === "text") {
        const formData = new FormData();
        formData.append("eventId", eventId);
        formData.append("guestName", guestName);
        formData.append("type", "text");
        formData.append("content", textContent);
        await api.post("/api/media/upload", formData);
      } else {
        for (let i = 0; i < files.length; i++) {
          setUploadProgress({ current: i + 1, total: files.length });
          const formData = new FormData();
          formData.append("eventId", eventId);
          formData.append("guestName", guestName);
          formData.append("type", mediaType);
          formData.append("file", files[i]);
          await api.post("/api/media/upload", formData);
        }
      }

      setFiles([]);
      setTextContent("");
      setAudioBlob(null);
      showToast("تم الرفع بنجاح وهتظهر في الألبوم.", "success");
      setShowViralCTA(true);
      onUpload();
    } catch (err) {
      console.error("Upload Error:", err);
      const serverMessage =
        err.response?.data?.error || err.response?.data?.message || "";
      showToast(
        `الرفع فشل: ${serverMessage || "حاول تاني بعد شوية."}`,
        "danger"
      );
    } finally {
      setLoading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  if (showViralCTA) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card shadow-lg border-0 p-4 p-md-5 text-center mb-4 bg-primary text-white rounded-celebration"
        dir="rtl"
      >
        <div
          className="display-4 mb-3 fw-black"
          style={{ fontFamily: "var(--font-headline)" }}
        >
          ✨ شكرًا يا {guestName}!
        </div>
        <p className="fs-4 mb-4 arabic-leading">
          صورتك اترفعت وهتفضل ذكرى حلوة في الألبوم.
        </p>
        <hr className="bg-white opacity-25 mb-4" />
        <p className="fw-bold mb-3 fs-5">
          عايز تعمل ألبوم زي ده لمناسبتك إنت كمان؟
        </p>
        <div className="d-flex flex-column gap-3 maxWidth-400 mx-auto">
          <Link
            to="/register"
            className="btn btn-light fw-bold py-3 rounded-pill text-primary shadow"
          >
            أيوه، عايز أعرف أكتر 🚀
          </Link>
          <button
            onClick={() => setShowViralCTA(false)}
            className="btn btn-link text-white text-decoration-none small fw-bold"
          >
            رفع صورة تانية
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      id="upload-section"
      className="rounded-celebration overflow-hidden shadow-lg mb-5 mx-1"
      dir="rtl"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* ── Event Blueprint Banner ── */}
      {eventData?.eventPhoto && (
        <div
          className="position-relative overflow-hidden"
          style={{ height: "220px" }}
        >
          {/* Background image blurred */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${eventData.eventPhoto})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(6px) brightness(0.55)",
              transform: "scale(1.08)",
            }}
          />

          {/* Gradient overlay */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.65) 100%)",
            }}
          />

          {/* Centered sharp thumbnail + event info */}
          <div className="position-relative h-100 d-flex align-items-center justify-content-center gap-4 px-4">
            {/* Sharp portrait thumbnail */}
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15 }}
              style={{
                width: "100px",
                height: "130px",
                borderRadius: "12px",
                overflow: "hidden",
                border: "3px solid rgba(255,255,255,0.9)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                flexShrink: 0,
              }}
            >
              <img
                src={eventData.eventPhoto}
                alt={eventData.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </motion.div>

            {/* Text block */}
            <motion.div
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.25 }}
              className="text-white text-end"
            >
              {/* Live badge */}
              <div className="d-flex align-items-center gap-2 justify-content-end mb-2">
                <span
                  className="fw-bold"
                  style={{
                    fontSize: "0.7rem",
                    letterSpacing: "0.12em",
                    opacity: 0.75,
                  }}
                >
                  ALBUM LIVE
                </span>
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "#4ade80",
                    boxShadow: "0 0 6px #4ade80",
                    animation: "pulse-dot 1.5s infinite",
                  }}
                />
              </div>

              <h2
                className="fw-black mb-1"
                style={{
                  fontFamily: "var(--font-headline)",
                  fontSize: "clamp(1.1rem, 4vw, 1.7rem)",
                  lineHeight: 1.2,
                }}
              >
                {eventData.name}
              </h2>

              {eventData.clientName && (
                <p
                  className="mb-1 opacity-85"
                  style={{ fontSize: "0.9rem", fontWeight: 600 }}
                >
                  {eventData.clientName}
                </p>
              )}

              <div
                className="d-flex flex-wrap gap-3 justify-content-end mt-2"
                style={{ fontSize: "0.8rem", opacity: 0.8 }}
              >
                {eventData.eventDate && (
                  <span className="d-flex align-items-center gap-1">
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "0.95rem" }}
                    >
                      calendar_month
                    </span>
                    {new Date(eventData.eventDate).toLocaleDateString("ar-EG", {
                      dateStyle: "medium",
                    })}
                  </span>
                )}
                {eventData.locationName && (
                  <span className="d-flex align-items-center gap-1">
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "0.95rem" }}
                    >
                      location_on
                    </span>
                    {eventData.locationName}
                  </span>
                )}
              </div>
            </motion.div>
          </div>

          <style>{`
            @keyframes pulse-dot {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.5; transform: scale(1.4); }
            }
          `}</style>
        </div>
      )}

      {/* ── Upload Form ── */}
      <div
        style={{
          background: "var(--surface-container-lowest)",
          border: "1px solid var(--outline-variant)",
          borderTop: eventData?.eventPhoto ? "none" : undefined,
          borderRadius: eventData?.eventPhoto
            ? "0 0 2rem 2rem"
            : "var(--radius-celebration)",
        }}
      >
        <div className="p-4 p-md-5">
          <label
            className="form-label fw-black fs-4 mb-4 text-on-surface"
            style={{ fontFamily: "var(--font-headline)" }}
          >
            إضافة مشاركة جديدة ✨
          </label>

          <div className="position-relative mb-4">
            <span className="material-symbols-outlined position-absolute top-50 translate-middle-y start-0 ms-3 text-muted" style={{ zIndex: 1 }}>
              person
            </span>
            <input
              className="form-control bg-surface-container-low border-0 ps-5"
              placeholder="اكتب اسمك هنا..."
              style={{ padding: "1rem 1.5rem", paddingLeft: "3rem", borderRadius: "1rem" }}
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
            />
          </div>

          {/* Media type tabs */}
          <div className="nav nav-pills nav-fill mb-4 bg-surface-container-high p-1 rounded-pill shadow-inner">
            {[
              { type: "photo", label: "📷 صور" },
              { type: "video", label: "🎥 فيديو" },
              { type: "voice", label: "🎙️ صوت" },
              { type: "text", label: "✍️ كلمة" },
            ].map(({ type, label }) => (
              <motion.button
                key={type}
                className="nav-link fw-bold p-2 rounded-pill"
                style={{
                  backgroundColor:
                    mediaType === type
                      ? "var(--surface-container-lowest)"
                      : "transparent",
                  color:
                    mediaType === type
                      ? "var(--primary)"
                      : "var(--on-surface-variant)",
                }}
                onClick={() => setMediaType(type)}
                whileTap={{ scale: 0.95 }}
              >
                {label}
              </motion.button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {(mediaType === "photo" || mediaType === "video") && (
              <motion.div
                className="mb-4"
                key={`${mediaType}-upload`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
              >
                <div
                  className={`drag-drop-zone ${isDragging ? 'dragging' : ''} ${files.length > 0 ? 'has-files' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const droppedFiles = Array.from(e.dataTransfer.files).filter(f =>
                      mediaType === "photo" ? f.type.startsWith("image/") : f.type.startsWith("video/")
                    );
                    setFiles(prev => [...prev, ...droppedFiles]);
                  }}
                  onClick={() => document.getElementById("file-input").click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept={mediaType === "photo" ? "image/*" : "video/*"}
                    multiple
                    className="d-none"
                    onChange={(e) => setFiles(Array.from(e.target.files))}
                  />
                  <div className="zone-content">
                    <span className="material-symbols-outlined zone-icon">
                      {mediaType === "photo" ? "add_a_photo" : "video_call"}
                    </span>
                    <p className="zone-text">
                      {files.length > 0
                        ? `تم اختيار ${files.length} ملف`
                        : `اسحب ال${mediaType === "photo" ? "صور" : "فيديوهات"} هنا أو اضغط للاختيار`}
                    </p>
                  </div>
                </div>
                {mediaType === "photo" && (
                  <p className="text-muted small mt-2">
                    تقدر تختار أكتر من صورة مرة واحدة.
                  </p>
                )}
              </motion.div>
            )}

            {mediaType === "voice" && (
              <motion.div
                className="text-center mb-4"
                key="voice-upload"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
              >
                <p className="text-on-surface-variant mb-3 fw-bold small">
                  سجل كلمة حلوة لأصحاب المناسبة
                </p>
                {!audioBlob ? (
                  <motion.button
                    className={`btn ${
                      isRecording ? "btn-danger" : "btn-outline-primary"
                    } w-100 py-4 shadow-sm fw-bold rounded-celebration`}
                    onClick={isRecording ? stopRecording : startRecording}
                    animate={isRecording ? { scale: [1, 1.02, 1] } : {}}
                    transition={{ repeat: Infinity, duration: 1 }}
                  >
                    {isRecording ? "⏹️ وقف التسجيل" : "🎤 سجل رسالتك الآن"}
                  </motion.button>
                ) : (
                  <div className="p-4 bg-surface-container-low rounded-celebration border border-primary-dim shadow-sm">
                    <audio
                      controls
                      src={URL.createObjectURL(audioBlob)}
                      className="w-100 mb-3"
                    />
                    <button
                      className="btn btn-outline-primary btn-sm w-100 rounded-pill py-2"
                      onClick={() => {
                        setAudioBlob(null);
                        setFiles([]);
                      }}
                    >
                      إعادة التسجيل
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {mediaType === "text" && (
              <motion.textarea
                className="form-control mb-4"
                rows="3"
                placeholder="اكتب تهنئة أو كلمة للذكرى..."
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                key="text-upload"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
              />
            )}
          </AnimatePresence>

          <style>{`
            .drag-drop-zone {
              border: 2px dashed #ffdad4;
              border-radius: 1.5rem;
              padding: 3rem 1.5rem;
              text-align: center;
              cursor: pointer;
              transition: all .2s;
              background: #fff;
            }
            .drag-drop-zone.dragging {
              border-color: #8b493e;
              background: #fff8f5;
              transform: scale(1.02);
            }
            .drag-drop-zone.has-files {
              border-style: solid;
              border-color: #4ade80;
              background: #f0fdf4;
            }
            .zone-icon { font-size: 3rem; color: #8b493e; margin-bottom: 1rem; }
            .zone-text { font-weight: 700; color: #534340; margin: 0; }
            .drag-drop-zone.has-files .zone-icon,
            .drag-drop-zone.has-files .zone-text { color: #166534; }
          `}</style>

          {loading && uploadProgress.total > 1 && (
            <div
              className="progress mb-4 shadow-sm"
              style={{ height: "12px", borderRadius: "10px" }}
            >
              <motion.div
                className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                role="progressbar"
                initial={{ width: 0 }}
                animate={{
                  width: `${
                    (uploadProgress.current / uploadProgress.total) * 100
                  }%`,
                }}
              />
            </div>
          )}

          <motion.button
            className="btn btn-primary w-100 py-3 fw-black fs-5 shadow-lg"
            style={{ fontFamily: "var(--font-headline)" }}
            onClick={handleUpload}
            disabled={loading || (mediaType === "voice" && isRecording)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? "جاري الرفع... ⌛" : "مشاركة الآن 🚀"}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default GuestUpload;
